import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronDown, ChevronRight, Plus, Trash2, Edit3, X,
  BookOpen, Home as HomeIcon, Sparkles, Heart, HeartOff,
  Loader2, Filter, Camera, Image as ImageIcon,
} from 'lucide-react';
import KonMariNodeRow from './KonMariNodeRow';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---- Instruktions-vy ----
function InstructionsView({ content }) {
  if (!content) return null;
  return (
    <div className="space-y-4" data-testid="konmari-instructions-view">
      {/* Intro */}
      <section className="neo-card p-4 bg-gradient-to-br from-[#FFF7E6] via-[#FFF0F6] to-[#F5EEFF]" data-testid="konmari-intro">
        <p className="text-[10px] font-bold tracking-[0.25em] uppercase opacity-60">Vägledning</p>
        <h2 className="font-['Fredoka'] font-bold text-2xl mt-1">{content.intro.title}</h2>
        <p className="text-sm opacity-70 mt-0.5">{content.intro.subtitle}</p>
        <p className="text-sm mt-3 leading-relaxed">{content.intro.body}</p>
        <div className="mt-4 text-center py-3 px-4 rounded-lg bg-white/70 border-2 border-dashed border-[#D06892]">
          <p className="font-['Fredoka'] font-bold text-base">✨ {content.intro.mantra}</p>
        </div>
      </section>

      {/* 5 steg */}
      <section className="neo-card p-4 bg-white" data-testid="konmari-steps">
        <h3 className="font-['Fredoka'] font-bold text-lg mb-3">De 5 stegen (i denna ordning!)</h3>
        <ol className="space-y-3">
          {content.steps.map((s) => (
            <li
              key={s.n}
              className="neo-card p-3 border-l-4"
              style={{ backgroundColor: `${s.color}44`, borderLeftColor: s.color }}
              data-testid={`konmari-step-${s.n}`}
            >
              <div className="flex items-baseline gap-3 mb-1">
                <span
                  className="w-8 h-8 rounded-full flex items-center justify-center text-lg font-bold shrink-0"
                  style={{ backgroundColor: s.color, color: 'rgba(0,0,0,0.75)' }}
                >
                  {s.n}
                </span>
                <h4 className="font-bold text-base">{s.emoji} {s.title}</h4>
              </div>
              <p className="text-sm opacity-80 mt-1 leading-relaxed">{s.desc}</p>
              {s.tips && s.tips.length > 0 && (
                <ul className="mt-2 space-y-0.5 pl-3">
                  {s.tips.map((tip, i) => (
                    <li key={i} className="text-xs opacity-70">🌸 {tip}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ol>
      </section>

      {/* Frågor att ställa sig */}
      <section className="neo-card p-4 bg-[#FFF0F6]" data-testid="konmari-questions">
        <h3 className="font-['Fredoka'] font-bold text-lg mb-2 flex items-center gap-2">
          <Sparkles className="w-5 h-5" /> Frågor att ställa varje sak
        </h3>
        <ul className="space-y-1.5">
          {content.questions.map((q, i) => (
            <li key={i} className="text-sm bg-white/70 rounded-md px-3 py-2 border-l-2 border-[#D06892]">
              <span className="font-bold mr-1">{i + 1}.</span> {q}
            </li>
          ))}
        </ul>
      </section>

      {/* Avslutande varma ord */}
      <section className="neo-card p-4 bg-gradient-to-br from-[#FFE8E8] to-[#FFF0F6] text-center" data-testid="konmari-closing">
        <p className="text-sm italic leading-relaxed">{content.closing}</p>
      </section>
    </div>
  );
}


// ---- Rum/Inventarie-vy (Excel-ish nested tree) ----

const LEVEL_META = {
  room:      { emoji: '🏠', label: 'Rum', color: '#FFC8DD', border: '#D06892' },
  container: { emoji: '📦', label: 'Förvaring', color: '#A2D2FF', border: '#4A8BC7' },
  item:      { emoji: '✨', label: 'Sak', color: '#FFE8A3', border: '#E0A330' },
};

const STATUS_META = {
  keep:    { emoji: '💗', label: 'Behåller', color: '#B9FBC0' },
  donate:  { emoji: '🎁', label: 'Skänker', color: '#A2D2FF' },
  discard: { emoji: '🗑️', label: 'Slänger', color: '#FFE8A3' },
  unsure:  { emoji: '🤔', label: 'Osäker', color: '#E4D6F5' },
};

function NodeEditor({ existing, parent, suggestedLevel, onSave, onClose }) {
  const isEdit = !!existing;
  const level = existing?.level || suggestedLevel || 'room';
  const [name, setName] = useState(existing?.name || '');
  const [emoji, setEmoji] = useState(existing?.emoji || LEVEL_META[level].emoji);
  const [notes, setNotes] = useState(existing?.notes || '');
  const [qty, setQty] = useState(existing?.qty || 1);
  const [status, setStatus] = useState(existing?.status || 'keep');
  const [sparkJoy, setSparkJoy] = useState(existing?.spark_joy === undefined ? null : existing.spark_joy);
  const [photo, setPhoto] = useState(existing?.photo || null);
  const [goalPhoto, setGoalPhoto] = useState(existing?.goal_photo || null);
  const [busy, setBusy] = useState(false);
  const [photoBusyKey, setPhotoBusyKey] = useState(null); // 'photo' | 'goal' | null

  const handlePhoto = (e, slot) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Välj en bild');
      return;
    }
    setPhotoBusyKey(slot);
    const setter = slot === 'goal' ? setGoalPhoto : setPhoto;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const maxW = 1280;
        let w = img.width;
        let h = img.height;
        if (w > maxW) {
          h = Math.round(h * (maxW / w));
          w = maxW;
        }
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.72);
        if (dataUrl.length > 2_400_000) {
          setter(canvas.toDataURL('image/jpeg', 0.55));
        } else {
          setter(dataUrl);
        }
        setPhotoBusyKey(null);
      };
      img.onerror = () => { toast.error('Kunde inte läsa bilden'); setPhotoBusyKey(null); };
      img.src = reader.result;
    };
    reader.onerror = () => { toast.error('Kunde inte läsa filen'); setPhotoBusyKey(null); };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const body = {
        name: name.trim(),
        emoji: emoji.trim() || null,
        notes: notes.trim() || null,
        photo: photo || '',
        goal_photo: goalPhoto || '',
      };
      if (level === 'item') {
        body.qty = Number(qty) || 1;
        body.status = status;
        body.spark_joy = sparkJoy;
      }
      if (isEdit) {
        const res = await axios.patch(`${API}/konmari/nodes/${existing.id}`, body, { withCredentials: true });
        toast.success('Uppdaterad 💗');
        onSave(res.data.item);
      } else {
        body.level = level;
        body.parent_id = parent?.id || null;
        const res = await axios.post(`${API}/konmari/nodes`, body, { withCredentials: true });
        toast.success('Tillagd ✨');
        onSave(res.data.item);
      }
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunde inte spara');
    } finally {
      setBusy(false);
    }
  };

  const title = isEdit
    ? `Ändra ${LEVEL_META[level].label.toLowerCase()}`
    : `Ny ${LEVEL_META[level].label.toLowerCase()}${parent ? ` i "${parent.name}"` : ''}`;

  return (
    <div className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-3" onClick={onClose} data-testid="konmari-editor-backdrop">
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="neo-card bg-white p-4 w-full max-w-md space-y-3"
        data-testid="konmari-editor"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-['Fredoka'] font-bold text-lg">{title}</h3>
          <button type="button" onClick={onClose} className="p-1"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex gap-2">
          <input
            type="text"
            value={emoji}
            onChange={(e) => setEmoji(e.target.value)}
            placeholder="🌸"
            className="neo-input w-16 text-center text-xl"
            maxLength={4}
            data-testid="konmari-editor-emoji"
          />
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={level === 'room' ? 'T.ex. Sovrum' : level === 'container' ? 'T.ex. Byrå, Låda 1' : 'T.ex. Ullsockor'}
            className="neo-input flex-1"
            autoFocus
            data-testid="konmari-editor-name"
          />
        </div>

        {level === 'item' && (
          <>
            <div className="flex gap-2 items-center">
              <label className="text-xs font-bold opacity-70">Antal:</label>
              <input
                type="number"
                min={1}
                max={999}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                className="neo-input w-20 text-center"
                data-testid="konmari-editor-qty"
              />
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase opacity-60 mb-1">Status</p>
              <div className="grid grid-cols-4 gap-1" data-testid="konmari-editor-status">
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setStatus(k)}
                    className={`rounded-md p-2 text-[10px] font-bold flex flex-col items-center gap-0.5 border-2 transition-all ${status === k ? 'ring-2 ring-[#D06892]' : ''}`}
                    style={{ backgroundColor: v.color, borderColor: status === k ? '#D06892' : 'rgba(0,0,0,0.1)' }}
                    data-testid={`konmari-status-${k}`}
                  >
                    <span className="text-base leading-none">{v.emoji}</span>
                    <span>{v.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase opacity-60 mb-1">Sparks joy?</p>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setSparkJoy(true)}
                  className={`flex-1 py-2 rounded-md border-2 text-xs font-bold flex items-center justify-center gap-1 transition-all ${sparkJoy === true ? 'bg-[#FFD9E6] border-[#D06892] ring-2 ring-[#D06892]/40' : 'bg-white border-black/10'}`}
                  data-testid="konmari-joy-yes"
                >
                  <Heart className="w-4 h-4 fill-[#D06892] text-[#D06892]" /> Ja, glädje
                </button>
                <button
                  type="button"
                  onClick={() => setSparkJoy(false)}
                  className={`flex-1 py-2 rounded-md border-2 text-xs font-bold flex items-center justify-center gap-1 transition-all ${sparkJoy === false ? 'bg-gray-200 border-gray-500 ring-2 ring-gray-500/40' : 'bg-white border-black/10'}`}
                  data-testid="konmari-joy-no"
                >
                  <HeartOff className="w-4 h-4" /> Nej
                </button>
                <button
                  type="button"
                  onClick={() => setSparkJoy(null)}
                  className={`flex-1 py-2 rounded-md border-2 text-xs font-bold transition-all ${sparkJoy === null ? 'bg-[#E4D6F5] border-[#8B63C4] ring-2 ring-[#8B63C4]/40' : 'bg-white border-black/10'}`}
                  data-testid="konmari-joy-unsure"
                >
                  🤔 Vet ej
                </button>
              </div>
            </div>
          </>
        )}

        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Anteckningar (valfritt)"
          className="neo-input w-full text-sm"
          rows={2}
          data-testid="konmari-editor-notes"
        />

        {/* Photo uploads — Nuläge + Mål for before/after comparison */}
        <div data-testid="konmari-editor-photo-section" className="space-y-2">
          <p className="text-[11px] font-bold uppercase opacity-60 flex items-center gap-1">
            <Camera className="w-3 h-3" /> Foton
          </p>
          <div className="grid grid-cols-2 gap-2">
            {/* NULÄGE */}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider opacity-60 mb-1">Nu</p>
              {photo ? (
                <div className="relative neo-card p-1 bg-white">
                  <img src={photo} alt="nu" className="max-h-32 w-full rounded object-cover" data-testid="konmari-editor-photo-preview" />
                  <button
                    type="button"
                    onClick={() => setPhoto(null)}
                    className="absolute -top-2 -right-2 bg-white rounded-full shadow border-2 border-red-300 p-1 hover:bg-red-50"
                    data-testid="konmari-editor-photo-remove"
                    title="Ta bort foto"
                  >
                    <X className="w-3 h-3 text-red-500" />
                  </button>
                </div>
              ) : (
                <label
                  className="neo-btn flex flex-col items-center justify-center gap-1 py-3 bg-[#E8F0FF] text-xs font-bold cursor-pointer hover:bg-[#D4E4FF] border-dashed border-2 border-[#4A8BC7]/40 min-h-[88px]"
                  data-testid="konmari-editor-photo-add"
                >
                  {photoBusyKey === 'photo'
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <ImageIcon className="w-4 h-4" />}
                  <span className="text-center leading-tight">{photoBusyKey === 'photo' ? 'Bearbetar…' : 'Hur det ser ut nu'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => handlePhoto(e, 'photo')}
                    className="hidden"
                    data-testid="konmari-editor-photo-input"
                    disabled={photoBusyKey !== null}
                  />
                </label>
              )}
            </div>

            {/* MÅL */}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider opacity-60 mb-1">Mål 🎯</p>
              {goalPhoto ? (
                <div className="relative neo-card p-1 bg-white">
                  <img src={goalPhoto} alt="mål" className="max-h-32 w-full rounded object-cover" data-testid="konmari-editor-goal-preview" />
                  <button
                    type="button"
                    onClick={() => setGoalPhoto(null)}
                    className="absolute -top-2 -right-2 bg-white rounded-full shadow border-2 border-red-300 p-1 hover:bg-red-50"
                    data-testid="konmari-editor-goal-remove"
                    title="Ta bort målbild"
                  >
                    <X className="w-3 h-3 text-red-500" />
                  </button>
                </div>
              ) : (
                <label
                  className="neo-btn flex flex-col items-center justify-center gap-1 py-3 bg-[#E6F4EA] text-xs font-bold cursor-pointer hover:bg-[#D2EBD9] border-dashed border-2 border-[#4DAA4D]/40 min-h-[88px]"
                  data-testid="konmari-editor-goal-add"
                >
                  {photoBusyKey === 'goal'
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <ImageIcon className="w-4 h-4" />}
                  <span className="text-center leading-tight">{photoBusyKey === 'goal' ? 'Bearbetar…' : 'Så här ska det se ut'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => handlePhoto(e, 'goal')}
                    className="hidden"
                    data-testid="konmari-editor-goal-input"
                    disabled={photoBusyKey !== null}
                  />
                </label>
              )}
            </div>
          </div>
          <p className="text-[10px] opacity-50 italic">
            💡 Spara en målbild när det är perfekt städat — sen kan du jämföra och hjärnan älskar before/after
          </p>
        </div>

        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onClose} className="neo-btn flex-1 py-2 bg-gray-100 text-sm">Avbryt</button>
          <button
            type="submit"
            disabled={busy || !name.trim()}
            className="neo-btn flex-1 py-2 bg-[#B9FBC0] font-bold text-sm"
            data-testid="konmari-editor-save"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Spara ✨'}
          </button>
        </div>
      </form>
    </div>
  );
}


function NodeRow(props) {
  return <KonMariNodeRow {...props} />;
}


function RoomsView() {
  const [nodes, setNodes] = useState([]);
  const [statsByRoom, setStatsByRoom] = useState({});
  const [loading, setLoading] = useState(true);
  const [editorState, setEditorState] = useState(null); // { mode: 'add'|'edit', parent?, existing?, suggestedLevel? }
  const [filterStatus, setFilterStatus] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/konmari/tree`, { withCredentials: true });
      setNodes(res.data.nodes || []);
      setStatsByRoom(res.data.stats_by_room || {});
    } catch {
      toast.error('Kunde inte ladda');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const childrenByParent = useMemo(() => {
    const map = new Map();
    const filtered = filterStatus === 'all'
      ? nodes
      : nodes.filter((n) => n.level !== 'item' || n.status === filterStatus);
    filtered.forEach((n) => {
      const arr = map.get(n.parent_id) || [];
      arr.push(n);
      map.set(n.parent_id, arr);
    });
    // Sort by order
    map.forEach((arr) => arr.sort((a, b) => (a.order || 0) - (b.order || 0)));
    return map;
  }, [nodes, filterStatus]);

  const rooms = childrenByParent.get(null) || [];

  const totalItems = nodes.filter((n) => n.level === 'item').length;
  const joyItems = nodes.filter((n) => n.level === 'item' && n.spark_joy === true).length;

  const handleDelete = async (node) => {
    const label = LEVEL_META[node.level].label.toLowerCase();
    const confirmMsg = node.level === 'room'
      ? `Ta bort "${node.name}" och ALLT i rummet?`
      : `Ta bort "${node.name}"${node.level === 'container' ? ' och allt den innehåller' : ''}?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await axios.delete(`${API}/konmari/nodes/${node.id}`, { withCredentials: true });
      toast.success(`${label} borttagen`);
      load();
    } catch {
      toast.error('Kunde inte ta bort');
    }
  };

  const handleToggleJoy = async (node) => {
    // Cycle: null -> true -> false -> null
    const next = node.spark_joy === null || node.spark_joy === undefined ? true : node.spark_joy === true ? false : null;
    try {
      await axios.patch(`${API}/konmari/nodes/${node.id}`, { spark_joy: next }, { withCredentials: true });
      load();
    } catch {
      toast.error('Kunde inte uppdatera');
    }
  };

  return (
    <div className="space-y-3" data-testid="konmari-rooms-view">
      {/* Summary */}
      <section className="neo-card p-3 bg-gradient-to-br from-[#FFF7E6] via-[#FFF0F6] to-[#F5EEFF] flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-[10px] font-bold tracking-[0.2em] uppercase opacity-60">Dina rum</p>
          <h3 className="font-['Fredoka'] font-bold text-lg">
            {rooms.length} rum · {totalItems} saker · 💗 {joyItems} som gnistrar
          </h3>
        </div>
        <button
          onClick={() => setEditorState({ mode: 'add', parent: null, suggestedLevel: 'room' })}
          className="neo-btn px-3 py-1.5 bg-[#FFC8DD] font-bold text-sm flex items-center gap-1"
          data-testid="konmari-add-room"
        >
          <Plus className="w-4 h-4" /> Nytt rum
        </button>
      </section>

      {/* Filter */}
      <section className="neo-card p-2 bg-white/70 flex items-center gap-2 flex-wrap" data-testid="konmari-filters">
        <span className="text-[10px] font-bold uppercase opacity-60 flex items-center gap-1">
          <Filter className="w-3 h-3" /> Visa saker:
        </span>
        <button
          onClick={() => setFilterStatus('all')}
          className={`text-[10px] font-bold px-2 py-0.5 rounded-full border-2 ${filterStatus === 'all' ? 'bg-white ring-2 ring-[#D06892]' : 'bg-white/50'}`}
          data-testid="konmari-filter-all"
        >
          Alla
        </button>
        {Object.entries(STATUS_META).map(([k, v]) => (
          <button
            key={k}
            onClick={() => setFilterStatus(k)}
            className={`text-[10px] font-bold px-2 py-0.5 rounded-full border-2 ${filterStatus === k ? 'ring-2 ring-[#D06892]' : ''}`}
            style={{ backgroundColor: v.color }}
            data-testid={`konmari-filter-${k}`}
          >
            {v.emoji} {v.label}
          </button>
        ))}
      </section>

      {/* Tree */}
      <section className="neo-card p-2 bg-white" data-testid="konmari-tree">
        {loading ? (
          <div className="py-12 flex flex-col items-center gap-2 opacity-60">
            <Loader2 className="w-6 h-6 animate-spin" />
            <p className="text-xs">Laddar dina rum…</p>
          </div>
        ) : rooms.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-base font-bold mb-1">🌸 Inga rum än</p>
            <p className="text-xs opacity-60 mb-3">Börja genom att lägga till ditt första rum</p>
            <button
              onClick={() => setEditorState({ mode: 'add', parent: null, suggestedLevel: 'room' })}
              className="neo-btn px-4 py-2 bg-[#FFC8DD] font-bold text-sm"
            >
              + Lägg till rum
            </button>
          </div>
        ) : (
          <div>
            {rooms.map((r) => (
              <NodeRow
                key={r.id}
                node={r}
                childrenByParent={childrenByParent}
                depth={0}
                statsByRoom={statsByRoom}
                onAddChild={(parent, suggestedLevel) =>
                  setEditorState({ mode: 'add', parent, suggestedLevel })
                }
                onEdit={(node) => setEditorState({ mode: 'edit', existing: node })}
                onDelete={handleDelete}
                onToggleJoy={handleToggleJoy}
              />
            ))}
          </div>
        )}
      </section>

      {editorState && (
        <NodeEditor
          existing={editorState.existing}
          parent={editorState.parent}
          suggestedLevel={editorState.suggestedLevel}
          onSave={() => load()}
          onClose={() => setEditorState(null)}
        />
      )}
    </div>
  );
}


// ---- Main KonMari Tab ----
export default function KonMariTab() {
  const [subtab, setSubtab] = useState('rooms'); // 'rooms' | 'instructions'
  const [instructions, setInstructions] = useState(null);

  useEffect(() => {
    axios.get(`${API}/konmari/instructions`, { withCredentials: true })
      .then((res) => setInstructions(res.data))
      .catch(() => {});
  }, []);

  return (
    <div className="space-y-3" data-testid="konmari-tab">
      {/* Header */}
      <header className="neo-card p-4 bg-gradient-to-br from-[#FFE8F5] via-[#F5EEFF] to-[#E8F0FF]" data-testid="konmari-header">
        <p className="text-[10px] font-bold tracking-[0.25em] uppercase opacity-60">KonMari</p>
        <h1 className="font-['Fredoka'] font-bold text-2xl mt-0.5">Sortera med kärlek 💗</h1>
        <p className="text-xs opacity-70 mt-1">Rum för rum, låda för låda — i din takt.</p>
      </header>

      {/* Sub-tab switch */}
      <div className="flex gap-2" data-testid="konmari-subtabs">
        <button
          onClick={() => setSubtab('rooms')}
          className={`neo-btn px-4 py-2 flex items-center gap-1.5 text-sm font-bold ${subtab === 'rooms' ? 'bg-[#FFC8DD]' : 'bg-white'}`}
          data-testid="konmari-subtab-rooms"
        >
          <HomeIcon className="w-4 h-4" /> Mina rum
        </button>
        <button
          onClick={() => setSubtab('instructions')}
          className={`neo-btn px-4 py-2 flex items-center gap-1.5 text-sm font-bold ${subtab === 'instructions' ? 'bg-[#CDB4DB]' : 'bg-white'}`}
          data-testid="konmari-subtab-instructions"
        >
          <BookOpen className="w-4 h-4" /> Instruktioner
        </button>
      </div>

      {subtab === 'rooms' && <RoomsView />}
      {subtab === 'instructions' && <InstructionsView content={instructions} />}
    </div>
  );
}
